import { Check, ChevronsUpDown, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Customer } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@nucleus-commerce/ui/components/command';
import { Popover, PopoverContent, PopoverTrigger } from '@nucleus-commerce/ui/components/popover';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { useCustomer } from '../hooks/use-customer';
import { useCustomers } from '../hooks/use-customers';
import { customerName } from '../schemas/customer-rules';

function SelectedCustomer({ storeId, id }: { storeId: string; id: string }) {
  const { data } = useCustomer(storeId, id);
  if (!data) return <span className="font-mono text-xs">{id}</span>;
  const name = customerName(data);
  return <span className="min-w-0 truncate">{name ? `${name} · ${data.email}` : data.email}</span>;
}

export interface CustomerPickerProps {
  storeId: string;
  value: string | null;
  onChange: (customer: Customer | null) => void;
  /** Shown while no customer is picked; defaults to "No customer". */
  placeholder?: string;
  id?: string;
  disabled?: boolean;
  'aria-invalid'?: boolean;
  /** Customers never offered, such as the record a merge starts from. */
  exclude?: readonly string[];
}

/**
 * One customer of the store, searched by name or email through the list endpoint (no 100-row
 * cap). Shared by draft orders (the quote's customer), gift cards (the card's holder) and the
 * customer merge (the duplicate).
 */
export function CustomerPicker({
  storeId,
  value,
  onChange,
  placeholder,
  id,
  disabled,
  'aria-invalid': ariaInvalid,
  exclude,
}: CustomerPickerProps) {
  const { t } = useTranslation('customers');
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(q.trim()), 250);
    return () => window.clearTimeout(handle);
  }, [q]);

  const query = useCustomers(storeId, { limit: 10, ...(debounced ? { q: debounced } : {}) });
  const options = (query.data?.items ?? []).filter((c) => !exclude?.includes(c.id));

  return (
    <div className="flex items-center gap-2">
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setQ('');
        }}
      >
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-invalid={ariaInvalid}
            disabled={disabled}
            className="min-w-0 flex-1 justify-between font-normal"
          >
            {value ? (
              <SelectedCustomer storeId={storeId} id={value} />
            ) : (
              <span className="text-muted-foreground">{placeholder ?? t('picker.none')}</span>
            )}
            <ChevronsUpDown className="opacity-50" aria-hidden />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput value={q} onValueChange={setQ} placeholder={t('picker.search')} />
            <CommandList>
              {query.isPending ? (
                <div className="flex flex-col gap-2 p-3">
                  <Skeleton className="h-6" />
                  <Skeleton className="h-6" />
                </div>
              ) : (
                <CommandEmpty>{t('picker.noResults')}</CommandEmpty>
              )}
              <CommandGroup>
                {options.map((customer) => {
                  const name = customerName(customer);
                  return (
                    <CommandItem
                      key={customer.id}
                      value={customer.id}
                      onSelect={() => {
                        onChange(customer);
                        setOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          'size-4',
                          value === customer.id ? 'opacity-100' : 'opacity-0',
                        )}
                        aria-hidden
                      />
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate">{name ?? customer.email}</span>
                        {name ? (
                          <span className="text-muted-foreground truncate text-xs">
                            {customer.email}
                          </span>
                        ) : null}
                      </span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {value && !disabled ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={t('picker.clear')}
          onClick={() => onChange(null)}
        >
          <X aria-hidden />
        </Button>
      ) : null}
    </div>
  );
}
